import React, { ReactNode, FC, useCallback } from 'react';

import PropTypes from 'prop-types';

import * as analytics from '@adsk/acs-web-analytics';
import { RenderIconType } from '@adsk/alloy-react-icon';
import { MenuList, MenuItem } from '@adsk/alloy-react-menu';
import theme, {
  StylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';

import { ANALYTICS_TYPES } from '../helpers/analytics';
import getAnchorRel from '../helpers/getAnchorRel';
import getAnchorTarget from '../helpers/getAnchorTarget';
import { productsListShape } from '../helpers/propTypes';
import { Product } from '../helpers/types';

// TODO - can be removed when account entitlement is available in V2
// TODO - verify it works as expected
const getProductIconV1 = (product: { renderIcon?: RenderIconType }) =>
  product?.renderIcon?.({
    size: 24,
  });

const parser = (products?: Product[], selectedId?: string) =>
  products?.map((product) => ({
    ...product,
    id: product.key,
    url: product.defaultUrl,
    highlighted: product.key === selectedId,
  })) || [];

type ProductPickerProps = StylableComponent<HTMLDivElement> & {
  products?: Product[];
  selectedId?: string;
  renderCustomHeader?: () => ReactNode;
  onProductClick?: ({ id, url }: { [key: string]: string }) => void;
  onClose?: () => void;
};

const ProductPicker: FC<ProductPickerProps> = ({
  className,
  style,
  products,
  selectedId,
  onClose,
  onProductClick,
  renderCustomHeader = () => null,
}) => {
  const onProductClickWithAnalytic = useCallback(
    (id: string) => {
      analytics.track(
        ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_PRODUCT_CLICK,
        {
          product_id: id,
          prev_product_id: selectedId,
        }
      );
    },
    [selectedId]
  );

  return (
    <div className={className} css={[{ width: 300 }, style]}>
      {renderCustomHeader()}
      <MenuList>
        {parser(products, selectedId).map(
          ({ id, name, url, highlighted, iconUrl, hasDivider, ...rest }) => (
            <MenuItem
              key={id}
              as={onProductClick ? 'button' : 'a'}
              data-testid={`ProductPicker__product-${id}`}
              href={url}
              target={url && getAnchorTarget(url)}
              rel={getAnchorRel(url)}
              highlighted={highlighted}
              nested={false}
              showSelection={false}
              onClick={() => {
                onProductClick?.({ id, url });
                onProductClickWithAnalytic(id);
                onClose?.();
              }}
              style={
                hasDivider
                  ? {
                      marginTop: 8,
                      paddingTop: 20,
                      paddingBottom: 20,
                      borderTop: `1px solid ${theme.colors.charcoal200}`,
                    }
                  : undefined
              }
            >
              {iconUrl ? (
                <span
                  css={[
                    {
                      maskImage: `url(${iconUrl})`,
                      backgroundColor: highlighted
                        ? theme.colors.white
                        : theme.colors.charcoal900,
                      width: 24,
                      height: 24,
                    },
                  ]}
                />
              ) : (
                getProductIconV1(rest)
              )}
              <div
                css={[
                  theme.typography.bodyMedium,
                  theme.typography.ellipsis,
                  {
                    flex: 1,
                    paddingLeft: 10,
                    textAlign: 'left',
                  },
                ]}
              >
                {name}
              </div>
            </MenuItem>
          )
        )}
      </MenuList>
    </div>
  );
};

ProductPicker.displayName = 'ProductPicker';

ProductPicker.defaultProps = {
  renderCustomHeader: () => null,
};

ProductPicker.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Products list */
  products: productsListShape,
  /** Selected product ID */
  selectedId: PropTypes.string,
  /** menu close handler */
  onClose: PropTypes.func,
  /** Custom product click handler, unsafe */
  onProductClick: PropTypes.func,
  /** render custom header */
  renderCustomHeader: PropTypes.func,
};

export default ProductPicker;
